<script setup>
import { Icon } from '@iconify/vue';
import { Link } from '@inertiajs/inertia-vue3';
import DashboardWidgetCard from './DashboardWidgetCard.vue';

defineProps({
    closeCalifications: {
        type: [Object, Array],
        default: () => ({}),
    },
    publishCalifications: {
        type: [Object, Array],
        default: () => ({}),
    },
    showCalifications: {
        type: [Object, Array],
        default: () => ({}),
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['reload']);

const bimestres = [
    { num: 1, label: 'Bimestre 1', closeKey: 'b1close', pubKey: 'b1publish' },
    { num: 2, label: 'Bimestre 2', closeKey: 'b2close', pubKey: 'b2publish' },
    { num: 3, label: 'Bimestre 3', closeKey: 'b3close', pubKey: 'b3publish' },
    { num: 4, label: 'Bimestre 4', closeKey: 'b4close', pubKey: 'b4publish' },
];
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <!-- 1. Cierre de Calificaciones -->
        <DashboardWidgetCard
            title="Cierre de Calificaciones"
            subtitle="Estado de bimestres cerrados"
            icon="tabler:lock-check"
            iconColor="emerald"
            :loading="loading"
            :error="error"
            @reload="emit('reload')"
        >
            <template #header-actions>
                <Link
                    :href="route('control-panel', { tab: 'cierre' })"
                    class="text-xs font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1 hover:underline"
                >
                    <span>Configurar</span>
                    <Icon icon="tabler:chevron-right" class="w-3.5 h-3.5" />
                </Link>
            </template>

            <div class="space-y-2">
                <Link
                    v-for="b in bimestres"
                    :key="b.num"
                    :href="route('control-panel', { tab: 'cierre' })"
                    class="flex items-center justify-between p-2.5 rounded-xl hover:bg-emerald-50/50 dark:hover:bg-slate-800/60 border border-gray-100 dark:border-slate-800 transition"
                >
                    <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">{{ b.label }}</span>
                    <div class="flex items-center gap-1.5">
                        <span
                            :class="closeCalifications[b.closeKey + 'ini'] == 1 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            INI
                        </span>
                        <span
                            :class="closeCalifications[b.closeKey + 'prim'] == 1 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            PRIM
                        </span>
                        <span
                            :class="closeCalifications[b.closeKey + 'sec'] == 1 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            SEC
                        </span>
                    </div>
                </Link>
            </div>
        </DashboardWidgetCard>

        <!-- 2. Publicación de Calificaciones -->
        <DashboardWidgetCard
            title="Publicar Calificaciones"
            subtitle="Visibilidad de notas por nivel"
            icon="tabler:eye-check"
            iconColor="purple"
            :loading="loading"
            :error="error"
            @reload="emit('reload')"
        >
            <template #header-actions>
                <Link
                    :href="route('control-panel', { tab: 'publicar' })"
                    class="text-xs font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400 flex items-center gap-1 hover:underline"
                >
                    <span>Configurar</span>
                    <Icon icon="tabler:chevron-right" class="w-3.5 h-3.5" />
                </Link>
            </template>

            <div class="space-y-2">
                <Link
                    v-for="b in bimestres"
                    :key="b.num"
                    :href="route('control-panel', { tab: 'publicar' })"
                    class="flex items-center justify-between p-2.5 rounded-xl hover:bg-purple-50/50 dark:hover:bg-slate-800/60 border border-gray-100 dark:border-slate-800 transition"
                >
                    <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">{{ b.label }}</span>
                    <div class="flex items-center gap-1.5">
                        <span
                            :class="publishCalifications[b.pubKey + 'ini'] == 1 ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            INI
                        </span>
                        <span
                            :class="publishCalifications[b.pubKey + 'prim'] == 1 ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            PRIM
                        </span>
                        <span
                            :class="publishCalifications[b.pubKey + 'sec'] == 1 ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                            class="text-[10px] px-2 py-0.5 rounded-md font-bold uppercase"
                        >
                            SEC
                        </span>
                    </div>
                </Link>
            </div>
        </DashboardWidgetCard>

        <!-- 3. Publicar Libretas -->
        <DashboardWidgetCard
            title="Publicar Libretas"
            subtitle="Emisión de libretas escolares"
            icon="tabler:file-certificate"
            iconColor="blue"
            :loading="loading"
            :error="error"
            @reload="emit('reload')"
            class="md:col-span-2 xl:col-span-1"
        >
            <template #header-actions>
                <Link
                    :href="route('control-panel', { tab: 'libretas' })"
                    class="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 hover:underline"
                >
                    <span>Configurar</span>
                    <Icon icon="tabler:chevron-right" class="w-3.5 h-3.5" />
                </Link>
            </template>

            <div class="space-y-2">
                <Link
                    :href="route('control-panel', { tab: 'libretas' })"
                    class="flex items-center justify-between p-2.5 rounded-xl hover:bg-blue-50/50 dark:hover:bg-slate-800/60 border border-gray-100 dark:border-slate-800 transition"
                >
                    <div>
                        <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Nivel Inicial</span>
                        <p v-if="showCalifications['dateini']" class="text-[11px] text-gray-400">
                            <span>{{ showCalifications['dateini'] }}</span>
                        </p>
                    </div>
                    <span
                        :class="showCalifications['showini'] == 1 ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                        class="text-xs px-3 py-1 rounded-md font-bold uppercase"
                    >
                        {{ showCalifications['showini'] == 1 ? 'Activo' : 'Inactivo' }}
                    </span>
                </Link>

                <Link
                    :href="route('control-panel', { tab: 'libretas' })"
                    class="flex items-center justify-between p-2.5 rounded-xl hover:bg-blue-50/50 dark:hover:bg-slate-800/60 border border-gray-100 dark:border-slate-800 transition"
                >
                    <div>
                        <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Nivel Primaria</span>
                        <p v-if="showCalifications['dateprim']" class="text-[11px] text-gray-400">
                            <span>{{ showCalifications['dateprim'] }}</span>
                        </p>
                    </div>
                    <span
                        :class="showCalifications['showprim'] == 1 ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                        class="text-xs px-3 py-1 rounded-md font-bold uppercase"
                    >
                        {{ showCalifications['showprim'] == 1 ? 'Activo' : 'Inactivo' }}
                    </span>
                </Link>

                <Link
                    :href="route('control-panel', { tab: 'libretas' })"
                    class="flex items-center justify-between p-2.5 rounded-xl hover:bg-blue-50/50 dark:hover:bg-slate-800/60 border border-gray-100 dark:border-slate-800 transition"
                >
                    <div>
                        <span class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Nivel Secundaria</span>
                        <p v-if="showCalifications['datesec']" class="text-[11px] text-gray-400">
                            <span>{{ showCalifications['datesec'] }}</span>
                        </p>
                    </div>
                    <span
                        :class="showCalifications['showsec'] == 1 ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300' : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'"
                        class="text-xs px-3 py-1 rounded-md font-bold uppercase"
                    >
                        {{ showCalifications['showsec'] == 1 ? 'Activo' : 'Inactivo' }}
                    </span>
                </Link>
            </div>
        </DashboardWidgetCard>
    </div>
</template>
